import { api } from "./api";
import type { Location } from "./locations-api";

// Re-export Location so callers that import it from settings-api continue to work
export type { Location };

// ════════════════════════════════════════════════════════════════════════════
// Settings API Types
// ════════════════════════════════════════════════════════════════════════════

export interface TenantSettings {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  timezone: string;
  logoUrl: string | null;
  brandPrimaryColor: string;
  brandSecondaryColor: string;
  brandBackgroundColor: string;
  brandTextColor: string;
  brandAccentColor: string;
  logoExtractedColors: string[];
  bookingWindowDays: number;
  cancellationCutoffHours: number;
  cancellationForfeitDeposit: boolean;
  rescheduleInsideCutoff: boolean;
  maxReschedules: number;
  defaultBufferMinutes: number;
  hidePricing: boolean;
  depositEnabled: boolean;
  depositType: "flat" | "percentage";
  depositAmount: string | null;
  noshowFeeEnabled: boolean;
  noshowFeeType: "full_price" | "custom_amount";
  noshowFeeAmount: string | null;
  reviewGoogleUrl: string | null;
  reviewYelpUrl: string | null;
  reviewDelayHours: number;
  reviewAutoSend: boolean;
  reviewEmailSubject: string | null;
  reviewEmailBody: string | null;
  reviewNotificationEmails: string[];
  // SMS notification settings
  smsEnabled: boolean;
  smsBookingConfirmation: boolean;
  smsReminder24h: boolean;
  smsReminder1h: boolean;
  smsCancellation: boolean;
  smsReschedule: boolean;
  // SMS message templates
  smsBookingConfirmationTemplate: string | null;
  smsReminder24hTemplate: string | null;
  smsReminder1hTemplate: string | null;
  smsCancellationTemplate: string | null;
  smsRescheduleTemplate: string | null;
  // Email reminder toggles and templates
  emailReminder24h: boolean;
  emailReminder1h: boolean;
  emailReminder24hBody: string | null;
  emailReminder1hBody: string | null;
  darkModeDefault: "light" | "dark" | "system";
  termsOfServiceUrl: string | null;
  privacyPolicyUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TeamMember {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  role: "owner" | "admin" | "manager" | "staff";
  avatarUrl: string | null;
  isActive: boolean;
  providerId: string | null;
  providerName: string | null;
  isPending: boolean;
  inviteExpiresAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface BusinessHour {
  id: string;
  dayOfWeek: number;
  startTime: string | null;
  endTime: string | null;
  isOpen: boolean;
}

export interface BrandingSettings {
  brandPrimaryColor: string;
  brandSecondaryColor: string;
  brandBackgroundColor: string;
  brandTextColor: string;
  brandAccentColor: string;
  logoUrl: string | null;
  logoExtractedColors: string[];
}

export interface EmbedOption {
  code: string;
  description: string;
}

export interface EmbedCodes {
  tenant: {
    slug: string;
    primaryColor: string;
    textColor: string;
    extractedColors: string[];
  };
  appointmentSystem: {
    directUrl: string;
    inline: EmbedOption;
    popupWidget: EmbedOption;
    popupText: EmbedOption;
  };
  chatbot: {
    directUrl: string;
    inline: EmbedOption;
    popupWidget: EmbedOption;
    popupText: EmbedOption;
  };
}

// ════════════════════════════════════════════════════════════════════════════
// Settings API Functions
// ════════════════════════════════════════════════════════════════════════════

// ──────────────────────────────────────────────────────────────────────────────
// General Settings
// ──────────────────────────────────────────────────────────────────────────────

export async function getSettings(): Promise<TenantSettings> {
  const response = await api<{ settings: TenantSettings }>("/api/settings");
  return response.settings;
}

export async function updateSettings(
  updates: Partial<
    Pick<
      TenantSettings,
      | "name"
      | "email"
      | "phone"
      | "timezone"
      | "bookingWindowDays"
      | "defaultBufferMinutes"
      | "hidePricing"
      | "cancellationCutoffHours"
      | "cancellationForfeitDeposit"
      | "rescheduleInsideCutoff"
      | "maxReschedules"
      | "depositEnabled"
      | "depositType"
      | "depositAmount"
      | "noshowFeeEnabled"
      | "noshowFeeType"
      | "noshowFeeAmount"
      | "reviewGoogleUrl"
      | "reviewYelpUrl"
      | "reviewDelayHours"
      | "reviewAutoSend"
      | "reviewEmailSubject"
      | "reviewEmailBody"
      | "reviewNotificationEmails"
      | "darkModeDefault"
      | "termsOfServiceUrl"
      | "privacyPolicyUrl"
    >
  >
): Promise<TenantSettings> {
  const response = await api<{ settings: TenantSettings }>("/api/settings", {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
  return response.settings;
}

// ──────────────────────────────────────────────────────────────────────────────
// Branding
// ──────────────────────────────────────────────────────────────────────────────

export async function updateBranding(
  branding: Partial<
    Pick<
      BrandingSettings,
      | "brandPrimaryColor"
      | "brandSecondaryColor"
      | "brandBackgroundColor"
      | "brandTextColor"
      | "brandAccentColor"
    >
  > & { logoExtractedColors?: string[] }
): Promise<BrandingSettings> {
  const response = await api<{ branding: BrandingSettings }>(
    "/api/settings/branding",
    {
      method: "PATCH",
      body: JSON.stringify(branding),
    }
  );
  return response.branding;
}

export async function updateExtractedColors(
  colors: string[]
): Promise<BrandingSettings> {
  const response = await api<{ branding: BrandingSettings }>(
    "/api/settings/branding",
    {
      method: "PATCH",
      body: JSON.stringify({ logoExtractedColors: colors }),
    }
  );
  return response.branding;
}

export async function uploadLogo(
  logoFile: File,
  extractedColors: string[]
): Promise<{ logoUrl: string; extractedColors: string[] }> {
  const formData = new FormData();
  formData.append("logo", logoFile);
  formData.append("extractedColors", JSON.stringify(extractedColors));

  const token = localStorage.getItem("af_access_token");
  const API_BASE = import.meta.env.VITE_API_URL || "";

  const response = await fetch(`${API_BASE}/api/settings/logo`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to upload logo");
  }

  return response.json();
}

export async function deleteLogo(): Promise<void> {
  await api<{ success: boolean }>("/api/settings/logo", {
    method: "DELETE",
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// Team Management
// ──────────────────────────────────────────────────────────────────────────────

export async function getTeam(): Promise<TeamMember[]> {
  const response = await api<{ team: TeamMember[] }>("/api/settings/team");
  return response.team;
}

export async function inviteTeamMember(data: {
  email: string;
  name?: string;
  role: "admin" | "manager" | "staff";
}): Promise<{ user: TeamMember; message: string }> {
  const response = await api<{ user: TeamMember; message: string }>(
    "/api/settings/team/invite",
    {
      method: "POST",
      body: JSON.stringify(data),
    }
  );
  return response;
}

export async function updateTeamMember(
  userId: string,
  updates: {
    name?: string;
    phone?: string | null;
    role?: "admin" | "manager" | "staff";
    isActive?: boolean;
    providerId?: string | null;
  }
): Promise<TeamMember> {
  const response = await api<{ user: TeamMember }>(
    `/api/settings/team/${userId}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    }
  );
  return response.user;
}

export async function deactivateTeamMember(userId: string): Promise<void> {
  await api<{ success: boolean }>(`/api/settings/team/${userId}`, {
    method: "DELETE",
  });
}

export async function resendTeamInvite(userId: string): Promise<{ inviteLink: string; message: string }> {
  return api<{ inviteLink: string; message: string }>(
    `/api/settings/team/${userId}/resend-invite`,
    { method: "POST" }
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Locations
// ──────────────────────────────────────────────────────────────────────────────

export async function getLocations(): Promise<Location[]> {
  const response = await api<{ locations: Location[] }>(
    "/api/settings/locations"
  );
  return response.locations;
}

export async function getLocationHours(
  locationId: string
): Promise<BusinessHour[]> {
  const response = await api<{ hours: BusinessHour[] }>(
    `/api/settings/locations/${locationId}/hours`
  );
  return response.hours;
}

export async function updateLocationHours(
  locationId: string,
  hours: Array<{
    dayOfWeek: number;
    startTime?: string | null;
    endTime?: string | null;
    isOpen?: boolean;
  }>
): Promise<BusinessHour[]> {
  const response = await api<{ hours: BusinessHour[] }>(
    `/api/settings/locations/${locationId}/hours`,
    {
      method: "PUT",
      body: JSON.stringify({ hours }),
    }
  );
  return response.hours;
}

// ──────────────────────────────────────────────────────────────────────────────
// Embed Codes
// ──────────────────────────────────────────────────────────────────────────────

export async function getEmbedCodes(): Promise<EmbedCodes> {
  const response = await api<{ embedCodes: EmbedCodes }>(
    "/api/settings/embed-code"
  );
  return response.embedCodes;
}

// ──────────────────────────────────────────────────────────────────────────────
// Onboarding
// ──────────────────────────────────────────────────────────────────────────────

export interface OnboardingStatus {
  status: "setup" | "active" | "suspended" | "deactivated";
  onboarding: {
    businessInfoComplete: boolean;
    stripeConnected: boolean;
    hasLocation: boolean;
    hasService: boolean;
    hasProvider: boolean;
  };
  progress: {
    required: number;
    completed: number;
    canComplete: boolean;
  };
}

export async function getOnboardingStatus(): Promise<OnboardingStatus> {
  const response = await api<OnboardingStatus>(
    "/api/settings/onboarding-status"
  );
  return response;
}

export async function completeOnboarding(): Promise<{
  success: boolean;
  message: string;
  status: string;
}> {
  const response = await api<{
    success: boolean;
    message: string;
    status: string;
  }>("/api/settings/complete-onboarding", {
    method: "POST",
  });
  return response;
}

// ──────────────────────────────────────────────────────────────────────────────
// Review Email Testing
// ──────────────────────────────────────────────────────────────────────────────

export interface TestEmailResponse {
  success: boolean;
  preview?: boolean;
  message: string;
  email?: {
    to: string;
    subject: string;
    body?: string;
  };
  error?: string;
}

export async function sendTestReviewEmail(email: string): Promise<TestEmailResponse> {
  const response = await api<TestEmailResponse>(
    "/api/settings/test-review-email",
    {
      method: "POST",
      body: JSON.stringify({ email }),
    }
  );
  return response;
}

// ──────────────────────────────────────────────────────────────────────────────
// SMS Testing
// ──────────────────────────────────────────────────────────────────────────────

export interface TestSmsResponse {
  success: boolean;
  message: string;
  messageSid?: string;
  error?: string;
}

export async function sendTestSms(
  phone: string,
  templateType: "booking" | "reminder24h" | "reminder1h" | "cancellation" | "reschedule"
): Promise<TestSmsResponse> {
  const response = await api<TestSmsResponse>(
    "/api/settings/test-sms",
    {
      method: "POST",
      body: JSON.stringify({ phone, templateType }),
    }
  );
  return response;
}
